<template >
    <!-- Custom alert in dialog to show some types of alerts: Success, Warning, Info, Error, Delete 
      PROPS:
      - Type: String (Loading, Info, Warning, Error, Success, Delete)
      - Title: String
      - Description: String
      - ConfirmText: String (Confirm for default, if not include a text)
      - CancelText: String (Cancel for default, if not include a text)
      - OnConfirmFn: Function (Function to action)
      - onCloseFn: Function (Called this function when the modal is closed. We blocked the close when the modal is the type loading)
    -->
  <!-- Dialog -->
  <el-dialog
    :visible.sync="visibleLocal"
    :close-on-click-modal="lockClose"
    :show-close="closeable"
    @close="onClose"
    class="custom-alert"
  >
    <div
      class="custom-alert__contain"
    >
      <!-- Icon -->
      <div 
        v-if="iconClass"  
        class="custom-alert__contain__icon-circle"
        :style="{
          'backgroundColor': backgroundColor
        }"
      >
        <i 
          class="custom-alert__contain__icon-circle__elem"
          :class="iconClass"
          :style="{
            'color': color
          }"
        />
      </div>

      <!-- Title -->
      <span 
        v-if="title"
        v-text="title"
        class="custom-alert__contain__title"
      />
      
      <!-- Description -->
      <span
        v-if="description"
        v-text="description"
        class="custom-alert__contain__description"
      />

      <!-- Confirm button -->
      <el-button 
        v-if="!isTypeLoading"
        class="custom-alert__contain__confirm"
        type="primary"
        @click="confirm"
      >
        <span v-text="confirmText"/>
      </el-button>

      <!-- Loading -->
      <div
        v-else
        class="custom-alert__contain__loading"
        v-loading="isTypeLoading"
      />
    </div>
  </el-dialog>
</template>

<script>

import { i18n } from '@shared/i18n';
import { get } from 'lodash';

export default {
  name: 'custom-alert',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    closeable: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'default'
    },
    title: {
      type: String,
    },
    description: {
      type: String,
    },
    confirmText: {
      type: String,
      default: i18n('common.confirm')
    },
    cancelText: {
      type: String,
      default: i18n('common.cancel')
    },
    onConfirmFn: {
      type: Function,
      default: () => {},
    },
    onCloseFn: {
      type: Function,
      default: () => {},
    },
    loading: {
      type: Boolean
    },
    visibleOnConfirm: {
      type: Boolean,
      default: false
    },
    lockClose: {
      type: Boolean,
      default: true,
    } 
  },

  data() {
    return {
      visibleLocal: false,
      configPerType: {
        success: {
          icon: 'icon-tick',
          color:'#2acc73',
          backgroundColor: this.convertHex('#2acc73', 20),
        },
        warning: {
          icon: 'icon-warning',
          color:'#ffcd07',
          backgroundColor: this.convertHex('#ffcd07', 20),
        },
        error: {
          icon: 'icon-warning',
          color:'#ee7266',
          backgroundColor: this.convertHex('#ee7266', 20),
        },
        info: {
          icon: 'icon-info',
          color: '#788bda',
          backgroundColor: this.convertHex('#788bda', 20),
        },
        delete: {
          icon: 'icon-trash',
          color:'#ee7266',
          backgroundColor: this.convertHex('#ee7266', 20),
        },
        edit: {
          icon: 'icon-trash',
          color:'#cccccc',
          backgroundColor: this.convertHex('#cccccc', 20),
        },
        loading: {
          icon: 'icon-clock',
          color:'#2acccc',
          backgroundColor: this.convertHex('#2acccc', 20)
        }
      }
    }
  },

  computed: {
    iconClass() {   
      return get(this.configPerType, `${this.type}.icon`);
    },
    color() {   
      return get(this.configPerType, `${this.type}.color`);
    },
    backgroundColor() {   
      return get(this.configPerType, `${this.type}.backgroundColor`);
    },
    isTypeLoading() {
      return this.type === 'loading'
    },
  },

  mounted () {
    this.visibleLocal = this.visible;
  },

  methods: {
    onClose() {
      this.$emit('update:visible', false);
      this.onCloseFn();
    },

    confirm() {
      this.onConfirmFn();
      this.$emit('update:visible', this.visibleOnConfirm);
    },

    i18n(val) {
      return i18n(val);
    },

    convertHex(hex, opacity){
      var hexNum = hex.replace('#','');
      var r = parseInt(hexNum.substring(0,2), 16);
      var g = parseInt(hexNum.substring(2,4), 16);
      var b = parseInt(hexNum.substring(4,6), 16);

      var result = 'rgba('+r+','+g+','+b+','+opacity/100+')';
      return result;
  }
    },

  watch: {
    visible(val) {
      this.visibleLocal = val;
    }
  },
};
</script>

<style lang="scss">
.custom-alert .el-dialog__header,
.custom-alert .el-dialog__body, 
.custom-alert .el-dialog__footer
{
  padding: 0;
}
</style>

<style lang="scss" scoped>
.custom-alert {
  &__contain { 
    padding: 48px 80px;
    display: grid;
    grid-auto-flow: row;
    grid-row-gap: 24px;
    justify-items: center;

    &__icon-circle {
      width: 76px;
      height: 76px;
      background: grey;
      display: grid;
      justify-items: center;
      align-items: center;
      border-radius: 50%;

      &__elem {
        font-size: 40px;
        justify-self: center;
      }
    }
    &__title {
      font-size: 24px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: normal;
      letter-spacing: normal;
      text-align: center;
      color: #686868;
      word-break: break-word;
    }
    &__description {
      font-size: 24px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: normal;
      letter-spacing: normal;
      text-align: center;
      color: #686868;
      word-break: break-word;
    }
    &__confirm {
      min-width: 211px;
      height: 38px;
      border-radius: 19px;
      background-color: #2acccc;
      color: #fff;
    }
    &__loading {
      justify-self: normal;
      margin: 24px; 
    }
  }
}
</style>

